import { BrandMark } from "@/components/BrandMark";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/Primitives";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); navigate("/"); } catch (ex) { setErr(errMsg(ex)); } finally { setLoading(false); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-secondary text-secondary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(hsl(0 0% 100%/.4) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%/.4) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative flex items-center gap-3">
          <BrandMark className="h-10 w-10" textClass="text-xl" testId="login-brand-mark" />
          <span className="leading-tight"><span className="font-heading font-semibold text-lg block">Yutaka CRM</span><span className="text-xs italic opacity-80 block">Grow Together be Stronger</span></span>
        </div>
        <div className="relative max-w-lg">
          <p className="eyebrow text-accent mb-4">Job Matching Vendor Siswa</p>
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight">Vendor mendaftarkan siswa. Sistem mencocokkan. Tim internal memutuskan.</h1>
          <p className="mt-6 text-secondary-foreground/75 leading-relaxed">Kontrol kualitas kandidat, antrian review internal, tracking proses penempatan ke Jepang, dan monitoring target serta fee per lembaga dalam satu tempat.</p>
        </div>
        <p className="relative text-xs text-secondary-foreground/50 font-mono">YUTAKA · PLACEMENT OPERATIONS</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <button data-testid="theme-toggle-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="absolute top-5 right-5 p-2 rounded-lg hover:bg-muted">
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <BrandMark className="h-10 w-10" textClass="text-xl" testId="login-brand-mark-mobile" />
            <span className="leading-tight"><span className="font-heading font-semibold text-lg block">Yutaka CRM</span><span className="text-xs italic text-primary block">Grow Together be Stronger</span></span>
          </div>
          <p className="eyebrow mb-2">Masuk</p>
          <h2 className="font-heading text-2xl font-semibold mb-8">Selamat datang kembali</h2>
          <div className="space-y-4">
            <Field label="Email"><Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@lembaga.id" required /></Field>
            <Field label="Password"><Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
            <div className="flex justify-end -mt-1"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-muted-foreground hover:text-primary">Lupa password?</Link></div>
            {err && <p data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{err}</p>}
            <Button data-testid="login-submit-button" type="submit" className="w-full h-11" disabled={loading}>{loading ? "Memproses..." : "Masuk"}</Button>
          </div>
          <div className="flex items-center gap-3 my-6"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">atau</span><div className="h-px flex-1 bg-border" /></div>
          <Button data-testid="google-login-button" type="button" variant="outline" className="w-full h-11" onClick={google}>
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Masuk dengan Google
          </Button>
        </form>
      </div>
    </div>
  );
}
